"use client";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, invariant } from "@/lib/utils";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronsUpDownIcon,
	CircleAlertIcon,
	GripVerticalIcon,
	Trash2Icon,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import useSWR from "swr";
import { categoriesBrowseAction, collectionsBrowseAction, pagesBrowseAction } from "./actions";
import type { MenuItem, PredefinedMenuItem } from "./schemas";

function SortableMenuItem({
	item,
	expandedItems,
	onAccordionChange,
	onDeleteItem,
	onItemUpdate,
	allowedTypes,
}: {
	item: MenuItem;
	expandedItems: string[];
	onAccordionChange: (id: string) => void;
	onDeleteItem: (id: string) => void;
	onItemUpdate: (id: string, newItem: MenuItem) => void;
	allowedTypes?: MenuItem["type"][];
}) {
	const { data: categoriesData } = useSWR("categoriesBrowseAction", () => categoriesBrowseAction(), {
		keepPreviousData: true,
	});
	const categories = categoriesData?.data?.map((c) => ({ refId: c.id, name: c.name, slug: c.slug }));
	console.log({ categories });

	const { data: collectionsData } = useSWR("collectionsBrowseAction", () => collectionsBrowseAction(), {
		keepPreviousData: true,
	});
	const collections = collectionsData?.data?.map((c) => ({ refId: c.id, name: c.name, slug: c.slug }));

	const { data: pagesData } = useSWR("pagesBrowseAction", () => pagesBrowseAction(), {
		keepPreviousData: true,
	});
	const pages = pagesData?.data?.map((c) => ({ refId: c.id, name: c.path, slug: c.path }));

	const data = {
		category: categories,
		"category-menu": categories,
		collection: collections,
		static: pages,
	} satisfies Record<PredefinedMenuItem["type"], undefined | { refId: string; name: string; slug: string }[]>;

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: item.id,
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
		zIndex: isDragging ? 10 : 1,
		opacity: isDragging ? 0.8 : 1,
	};

	const isExpanded = expandedItems.includes(item.id);

	const foundItem =
		item.type === "custom" || item.type === "category-dropdown"
			? item
			: data[item.type]?.find((i) => i.refId === item.refId);

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn("border rounded bg-card overflow-hidden", isDragging && "shadow-lg")}
		>
			<div className="flex items-center justify-between px-3 py-2">
				<div className="flex items-center gap-3">
					<div {...attributes} {...listeners} className="cursor-grab">
						<GripVerticalIcon className="h-4 w-4 text-muted-foreground" />
					</div>
					<div>
						<p className="text-sm font-medium">
							{!foundItem ? " " : ("refId" in foundItem ? foundItem.name : foundItem.label) || " "}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-6 w-6 p-0"
						onClick={(e) => {
							e.stopPropagation();
							onAccordionChange(item.id);
						}}
					>
						<ChevronDownIcon
							className={cn("h-4 w-4 transition-transform duration-200", isExpanded && "rotate-180")}
						/>
						<span className="sr-only">Toggle</span>
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={(e) => {
							e.stopPropagation();
							onDeleteItem(item.id);
						}}
					>
						<Trash2Icon className="h-4 w-4" />
						<span className="sr-only">Delete</span>
					</Button>
				</div>
			</div>

			{isExpanded && (
				<div className="pb-3 px-3 space-y-4 pt-2 border-t">
					<Tabs
						value={item.type === "custom" ? "custom" : "pages"}
						onValueChange={(value_) => {
							const value = value_ as "custom" | "pages";
							if (value === "custom") {
								onItemUpdate(item.id, { id: item.id, type: "custom", label: "", url: "" });
							} else {
								onItemUpdate(item.id, { id: item.id, type: "category", refId: undefined });
							}
						}}
						className="pt-3"
					>
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="pages">Pages</TabsTrigger>
							<TabsTrigger value="custom">Custom URL</TabsTrigger>
						</TabsList>
					</Tabs>

					{item.type !== "custom" ? (
						<div className="space-y-4">
							<div className="space-y-2">
								<Label>Page Type</Label>
								<Select
									value={item.type}
									onValueChange={(value: MenuItem["type"]) => {
										if (value === "custom") {
											onItemUpdate(item.id, { id: item.id, type: value, label: "", url: "" });
										} else if (value === "category-dropdown") {
											onItemUpdate(item.id, { id: item.id, type: value, label: "" });
										} else if (value === "category-menu") {
											onItemUpdate(item.id, { id: item.id, type: value, refId: undefined, expandable: true });
										} else {
											onItemUpdate(item.id, { id: item.id, type: value, refId: undefined });
										}
									}}
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Select page type" />
									</SelectTrigger>
									<SelectContent>
										{(!allowedTypes || allowedTypes.includes("category")) && (
											<SelectItem value="category">Single Category</SelectItem>
										)}
										{(!allowedTypes || allowedTypes.includes("collection")) && (
											<SelectItem value="collection">Single Collection</SelectItem>
										)}
										{(!allowedTypes || allowedTypes.includes("category-menu")) && (
											<SelectItem value="category-menu">Category Menu</SelectItem>
										)}
										{/* @todo */}
										{(!allowedTypes || allowedTypes.includes("static")) && (
											<SelectItem value="static">Static Page</SelectItem>
										)}
										{(!allowedTypes || allowedTypes.includes("category-dropdown")) && (
											<SelectItem value="category-dropdown" className="text-xs text-neutral-500">
												<Tooltip>
													<TooltipTrigger tabIndex={0} asChild className="p-0 m-0 h-auto w-auto">
														<Button type="button" variant="ghost" size="icon" className="p-0">
															<CircleAlertIcon className="size-4 stroke-gray-700" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Deprecated. Use Category Menu instead.</TooltipContent>
												</Tooltip>
												Categories Dropdown
											</SelectItem>
										)}
									</SelectContent>
								</Select>
							</div>
							{item.type === "category-dropdown" ? (
								<div className="space-y-2">
									<Label htmlFor={`label-${item.id}`}>Label</Label>
									<Input
										id={`label-${item.id}`}
										value={item.label}
										onChange={(e) => onItemUpdate(item.id, { ...item, label: e.target.value })}
										placeholder="Menu item label"
									/>
								</div>
							) : (
								<>
									<div className="space-y-2">
										<Label>Select {item.type === "category-menu" ? "category" : item.type}</Label>
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="outline"
													role="combobox"
													className="w-full justify-between"
												>
													{!foundItem
														? `Select a ${item.type === "category-menu" ? "category" : item.type}`
														: "refId" in foundItem
															? foundItem.name
															: foundItem.label}
													<ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
												</Button>
											</PopoverTrigger>
											<PopoverContent className="p-0 w-[317px]">
												<Command>
													<CommandInput
														placeholder={`Search ${item.type === "category-menu" ? "category" : item.type}...`}
													/>
													<CommandList>
														<CommandEmpty>No {item.type} found.</CommandEmpty>
														<CommandGroup>
															{(item.type === "category" || item.type === "category-menu"
																? categories
																: item.type === "collection"
																	? collections
																	: pages
															)?.map((selectableItem) => (
																// @todo indent categories
																<CommandItem
																	key={selectableItem.refId}
																	value={selectableItem.slug + " " + selectableItem.name + " " + item.refId}
																	onSelect={() => {
																		onItemUpdate(item.id, {
																			id: item.id,
																			type: item.type,
																			refId: selectableItem.refId,
																		});
																	}}
																>
																	<CheckIcon
																		className={cn(
																			"mr-2 h-4 w-4",
																			item.refId === selectableItem.refId ? "opacity-100" : "opacity-0",
																		)}
																	/>
																	{selectableItem.name}
																</CommandItem>
															))}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
									</div>

									{item.type === "category-menu" ? (
										<div className="space-y-2">
											<Label>Should category menu be expandable</Label>
											<Select
												onValueChange={(expandable) =>
													onItemUpdate(item.id, { ...item, expandable: expandable === "true" })
												}
												value={(item.expandable ?? false).toString()}
											>
												<SelectTrigger className="w-full">
													<SelectValue placeholder="Is expandable?" />
												</SelectTrigger>
												<SelectContent className="w-full">
													<SelectItem value="true">Yes</SelectItem>
													<SelectItem value="false">No</SelectItem>
												</SelectContent>
											</Select>
										</div>
									) : null}
								</>
							)}
						</div>
					) : (
						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor={`label-${item.id}`}>Label</Label>
								<Input
									id={`label-${item.id}`}
									value={item.label}
									onChange={(e) => onItemUpdate(item.id, { ...item, label: e.target.value })}
									placeholder="Menu item label"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor={`url-${item.id}`}>URL</Label>
								<Input
									id={`url-${item.id}`}
									value={item.url}
									onChange={(e) => onItemUpdate(item.id, { ...item, url: e.target.value })}
									placeholder="https://example.com/page"
								/>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export function MenuEditor({
	items,
	onChange,
	allowedTypes,
}: { items: MenuItem[]; onChange: (items: MenuItem[]) => void; allowedTypes?: MenuItem["type"][] }) {
	const [expandedItems, setExpandedItems] = useState<string[]>([]);

	// Set up sensors for drag and drop
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const addNewItem = () => {
		const newItem: MenuItem =
			allowedTypes?.[0] === "custom" || allowedTypes?.[0] === "category-dropdown"
				? {
						id: nanoid(),
						type: allowedTypes?.[0],
						label: "",
						url: "",
					}
				: {
						id: nanoid(),
						refId: undefined,
						type: allowedTypes?.[0] ?? "category",
					};

		// Add the new item to the menu items
		onChange([...items, newItem]);

		// Expand the accordion for the new item
		setExpandedItems([...expandedItems, newItem.id]);
	};

	const handleItemUpdate = (id: string, newItem: MenuItem) => {
		const updatedItems = items.map((item) => (item.id === id ? newItem : item));
		onChange(updatedItems);
	};

	const handleDeleteItem = (id: string) => {
		onChange(items.filter((item) => item.id !== id));
		setExpandedItems(expandedItems.filter((itemId) => itemId !== id));
	};

	const handleAccordionChange = (id: string) => {
		if (expandedItems.includes(id)) {
			setExpandedItems(expandedItems.filter((item) => item !== id));
		} else {
			setExpandedItems([...expandedItems, id]);
			const item = items.find((item) => item.id === id);
			invariant(item, "Item not found");
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over) return;

		if (active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);

			const result = arrayMove(items, oldIndex, newIndex);
			onChange(result);
		}
	};

	return (
		<div className="space-y-2">
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
					<div className="space-y-1">
						{items.map((item) => (
							<SortableMenuItem
								key={item.id}
								item={item}
								expandedItems={expandedItems}
								onAccordionChange={handleAccordionChange}
								onDeleteItem={handleDeleteItem}
								onItemUpdate={handleItemUpdate}
								allowedTypes={allowedTypes}
							/>
						))}
					</div>
				</SortableContext>
			</DndContext>

			<Button type="button" className="w-full rounded-full" onClick={addNewItem}>
				Add Menu Item
			</Button>
		</div>
	);
}
